import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

import { api, formatApiError } from "@/api/client";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";

// V1.1c — /admin/bank-statements
//
// Table view + side drawer. Read access via `view_credit_reports`,
// mutation gated by `edit_dispute_letters` per the backend permissions
// matrix.
const STATUS_STYLES = {
  uploaded: { bg: "rgba(252,211,77,0.10)", color: "#FCD34D", border: "rgba(252,211,77,0.30)" },
  verified: { bg: "rgba(134,239,172,0.10)", color: "#86EFAC", border: "rgba(134,239,172,0.30)" },
  rejected: { bg: "rgba(252,165,165,0.10)", color: "#FCA5A5", border: "rgba(252,165,165,0.30)" },
};


export default function AdminBankStatements() {
  const { has } = useAdminPermissions();
  const canMutate = has("edit_dispute_letters");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filters, setFilters] = useState({ user_email: "", status: "", has_period: "" });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.status) params.status = filters.status;
      if (filters.has_period) params.has_period = filters.has_period === "yes";
      const { data } = await api.get("/admin/bank-statements", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <AdminLayout
      title="Bank Statements"
      subtitle="User-uploaded bank statements. Verify, reject, or remove with full audit."
      actions={
        <button
          data-testid="admin-bank-statements-refresh"
          onClick={reload}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[var(--v2-border)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
        >
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Bank Statements"
          purpose="Review uploaded bank statements and the extracted financial insights."
          configures="View extraction results, delete statements, download originals."
          impact="Deleting a statement removes it from the member's Financial Analyst view."
          safety="Deleting is destructive. Original files are removed from storage on delete."
        />
      </div>
      
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--v2-text-muted)]" />
          <input
            data-testid="admin-bank-statements-search"
            value={filters.user_email}
            onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
            placeholder="Search by user email…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-[var(--v2-border)] bg-transparent text-sm text-[var(--v2-text)]"
          />
        </div>
        <select
          data-testid="admin-bank-statements-status-filter"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="rounded-md border border-[var(--v2-border)] bg-[var(--v2-surface)] px-2 py-2 text-xs text-[var(--v2-text-muted)]"
        >
          <option value="">All status</option>
          <option value="uploaded">Uploaded</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          data-testid="admin-bank-statements-period-filter"
          value={filters.has_period}
          onChange={(e) => setFilters((f) => ({ ...f, has_period: e.target.value }))}
          className="rounded-md border border-[var(--v2-border)] bg-[var(--v2-surface)] px-2 py-2 text-xs text-[var(--v2-text-muted)]"
        >
          <option value="">All periods</option>
          <option value="yes">Labelled</option>
          <option value="no">Unlabelled</option>
        </select>
      </div>

      <div className="rounded-xl border border-[var(--v2-border)] overflow-hidden" style={{ background: "var(--v2-surface)" }}>
        <table className="w-full text-sm">
          <thead style={{ background: "var(--v2-surface-subtle)" }}>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)]">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Filename</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Status</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Period</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Bank</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Size</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Uploaded</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--v2-border)]">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[var(--v2-text-muted)]">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[var(--v2-text-muted)]">No statements match these filters.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`admin-bank-statements-row-${it.id}`} className="hover:bg-[rgba(183,148,244,0.04)]">
                <td className="px-4 py-3 text-[var(--v2-text-muted)]">{it.user_email || it.user_id}</td>
                <td className="px-4 py-3 text-[var(--v2-text)]">{it.original_filename}</td>
                <td className="px-4 py-3 hidden md:table-cell"><StatusPill status={it.status} /></td>
                <td className="px-4 py-3 hidden md:table-cell text-[var(--v2-text-muted)]">
                  {it.statement_month && it.statement_year
                    ? new Date(it.statement_year, it.statement_month - 1).toLocaleString("en-US", { month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-[var(--v2-text-muted)]">{it.bank_name || "—"}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-[var(--v2-text-muted)]">{(it.file_size_bytes / 1024).toFixed(0)} KB</td>
                <td className="px-4 py-3 hidden lg:table-cell text-[var(--v2-text-muted)]">
                  {new Date(it.uploaded_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`admin-bank-statements-open-${it.id}`}
                    onClick={() => setOpenId(it.id)}
                    className="text-xs text-[#B794F4] hover:underline"
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId && (
        <BankStatementDrawer
          statementId={openId}
          canMutate={canMutate}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </AdminLayout>
  );
}


function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.uploaded;
  return (
    <span
      data-testid={`bank-statement-pill-${status}`}
      className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
      style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      {status === "verified" && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
      {status}
    </span>
  );
}


function BankStatementDrawer({ statementId, canMutate, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ status: "uploaded", admin_notes: "", rejection_reason: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/bank-statements/${statementId}`)
      .then(({ data }) => {
        setDetail(data);
        setForm({
          status: data.status,
          admin_notes: data.admin_notes || "",
          rejection_reason: data.rejection_reason || "",
        });
      })
      .catch((e) => { toast.error(formatApiError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [statementId, onClose]);

  async function save() {
    setSaving(true);
    try {
      const payload = { status: form.status, admin_notes: form.admin_notes };
      if (form.status === "rejected") payload.rejection_reason = form.rejection_reason;
      const { data } = await api.patch(`/admin/bank-statements/${statementId}/status`, payload);
      setDetail(data);
      toast.success("Status saved.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    setDeleting(true);
    try {
      await api.delete(`/admin/bank-statements/${statementId}`);
      toast.success("Statement deleted.");
      setConfirmingDelete(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  }

  function download() {
    const a = document.createElement("a");
    a.href = `/api/admin/bank-statements/${statementId}/download`;
    a.download = detail?.original_filename || "statement.pdf";
    a.click();
  }

  return (
    <div data-testid="admin-bank-statements-drawer" className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.66)" }}>
      <div className="w-full max-w-xl h-full overflow-y-auto" style={{ background: "var(--v2-background)", borderLeft: "1px solid var(--v2-border)" }}>
        <header className="sticky top-0 flex items-center justify-between border-b border-[var(--v2-border)] px-5 py-4 z-10" style={{ background: "var(--v2-background)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="admin-bank-statements-drawer-back"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#B794F4]">Bank statement</div>
              <div className="font-display text-base text-[var(--v2-text)] truncate">{detail?.original_filename || "…"}</div>
            </div>
          </div>
          <button data-testid="admin-bank-statements-drawer-close" onClick={onClose} className="text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]">
            <X className="w-5 h-5" />
          </button>
        </header>

        {loading || !detail ? (
          <div className="p-6 text-sm text-[var(--v2-text-muted)]">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <button
              type="button"
              data-testid="admin-bank-statements-drawer-download"
              onClick={download}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md font-medium"
              style={{ background: "#A78BFA", color: "var(--v2-accent-ink)" }}
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </button>

            <dl className="grid grid-cols-2 gap-3 text-xs">
              <DetailField label="User ID" value={detail.user_id} />
              <DetailField label="Size" value={`${(detail.file_size_bytes / 1024).toFixed(0)} KB`} />
              <DetailField
                label="Statement period"
                value={detail.statement_month && detail.statement_year
                  ? new Date(detail.statement_year, detail.statement_month - 1).toLocaleString("en-US", { month: "long", year: "numeric" })
                  : "—"}
              />
              <DetailField label="Bank" value={detail.bank_name || "—"} />
              <DetailField label="Uploaded" value={new Date(detail.uploaded_at).toLocaleString()} />
              <DetailField label="Verified" value={detail.verified_at ? new Date(detail.verified_at).toLocaleString() : "—"} />
            </dl>

            <fieldset className="space-y-3" disabled={!canMutate}>
              <div>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-1">Status</div>
                  <select
                    data-testid="admin-bank-statements-drawer-status"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)]"
                  >
                    <option value="uploaded">Uploaded (default)</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </label>
              </div>
              {form.status === "rejected" && (
                <div>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-1">Rejection reason (required)</div>
                    <textarea
                      data-testid="admin-bank-statements-drawer-reason"
                      value={form.rejection_reason}
                      onChange={(e) => setForm((f) => ({ ...f, rejection_reason: e.target.value }))}
                      rows={3}
                      placeholder="Tell the user why this was rejected…"
                      className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)]"
                    />
                  </label>
                </div>
              )}
              <div>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-1">Admin notes (internal)</div>
                  <textarea
                    data-testid="admin-bank-statements-drawer-notes"
                    value={form.admin_notes}
                    onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))}
                    rows={2}
                    placeholder="Optional admin-only notes."
                    className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)]"
                  />
                </label>
              </div>
            </fieldset>

            <div className="flex items-center justify-between gap-3 pt-4 border-t border-[var(--v2-border)]">
              <button
                type="button"
                data-testid="admin-bank-statements-drawer-delete"
                onClick={() => setConfirmingDelete(true)}
                disabled={!canMutate || deleting}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[var(--v2-border)] text-[#FCA5A5] hover:bg-[rgba(252,165,165,0.06)] disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" /> {deleting ? "Deleting…" : "Delete"}
              </button>
              <button
                type="button"
                data-testid="admin-bank-statements-drawer-save"
                onClick={save}
                disabled={!canMutate || saving}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50"
                style={{ background: "#A78BFA", color: "var(--v2-accent-ink)" }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={destroy}
        busy={deleting}
        variant="destructive"
        title="Delete this bank statement?"
        description="The uploaded PDF is removed from disk immediately. This action is recorded in the admin audit log."
        confirmLabel="Delete statement"
        testId="bank-statements-delete-confirm"
      />
    </div>
  );
}


function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-1">{label}</div>
      <div className="text-[var(--v2-text-muted)] break-all">{value}</div>
    </div>
  );
}
